/**
 * Rig States as one doc per record (S305, 2026-10-06; engine:
 * `sync/recordDocSync.ts`).
 *
 *   rigstate-bank-<id>   a bank's name and dates, NOT its looks
 *   rigstate-look-<id>   one look, with its bank and its position
 *
 * Per LOOK, not per bank, because capturing two looks into one bank on two
 * PCs in one sync window is the ordinary case (OPBridge S305, as answered),
 * and a per-bank doc would lose one of them.
 *
 * A look's position travels as `order` (its index when written). Placing
 * sorts a bank's looks by order, ties by id, then renumbers, so two looks
 * added at the end on two PCs land in the same order on both; the renumbered
 * one is pushed on the next pass and both converge.
 */

import type { RigStateBank, RigStateLibrary, RigStateLook } from '@opbuttons/schema';
import { idPart, type RecordAdapter } from '../sync/recordDocSync.ts';

const BANK = 'rigstate-bank-';
const LOOK = 'rigstate-look-';

export function bankDocId(id: string): string { return `${BANK}${idPart(id)}`; }
export function lookDocId(id: string): string { return `${LOOK}${idPart(id)}`; }

export interface BankRecord { id: string; name: string; created: string; modified: string }
export interface LookRecord { bankId: string; order: number; look: RigStateLook }

function isBank(r: unknown): r is BankRecord {
  const b = r as BankRecord;
  return !!b && typeof b === 'object' && typeof b.id === 'string' && typeof b.name === 'string';
}
function isLook(r: unknown): r is LookRecord {
  const l = r as LookRecord;
  return !!l && typeof l === 'object' && typeof l.bankId === 'string' && typeof l.order === 'number'
    && !!l.look && typeof l.look.id === 'string' && Array.isArray(l.look.values);
}

export const rigStateAdapter: RecordAdapter<RigStateLibrary> = {
  label: 'Rig States',
  prefixes: [BANK, LOOK],
  legacyDocId: 'rigstate',
  split(lib) {
    const out = new Map<string, unknown>();
    for (const b of lib.banks) {
      const rec: BankRecord = { id: b.id, name: b.name, created: b.created, modified: b.modified };
      out.set(bankDocId(b.id), rec);
      b.looks.forEach((look, order) => out.set(lookDocId(look.id), { bankId: b.id, order, look } satisfies LookRecord));
    }
    return out;
  },
  apply(lib, puts, dels) {
    // Banks: metadata replaced in place, new ones appended, deleted dropped.
    const banks: RigStateBank[] = [];
    const seen = new Set<string>();
    for (const b of lib.banks) {
      const id = bankDocId(b.id);
      seen.add(id);
      if (dels.has(id)) continue;
      const put = puts.get(id) as BankRecord | undefined;
      banks.push(put ? { ...b, name: put.name, created: put.created, modified: put.modified, looks: [...b.looks] } : { ...b, looks: [...b.looks] });
    }
    for (const [id, r] of puts) {
      if (!id.startsWith(BANK) || seen.has(id)) continue;
      const put = r as BankRecord;
      banks.push({ id: put.id, name: put.name, created: put.created, modified: put.modified, looks: [] });
    }

    // Looks: every look as (bank, order) - local ones at their index, the
    // incoming ones where they say - minus the deleted.
    const byBank = new Map<string, { order: number; look: RigStateLook }[]>();
    for (const b of banks) {
      byBank.set(b.id, b.looks
        .map((look, order) => ({ order, look }))
        .filter((x) => !dels.has(lookDocId(x.look.id)) && !puts.has(lookDocId(x.look.id))));
    }
    for (const [id, r] of puts) {
      if (!id.startsWith(LOOK)) continue;
      const rec = r as LookRecord;
      byBank.get(rec.bankId)?.push({ order: rec.order, look: rec.look }); // no bank here yet: left out, retried
    }
    for (const b of banks) {
      const xs = byBank.get(b.id)!;
      xs.sort((p, q) => p.order - q.order || (p.look.id < q.look.id ? -1 : p.look.id > q.look.id ? 1 : 0));
      b.looks = xs.map((x) => x.look);
    }
    return { ...lib, banks };
  },
  problem(docId, r) {
    if (docId.startsWith(BANK)) {
      if (!isBank(r)) return 'not a rig state bank';
      return bankDocId(r.id) === docId ? null : 'bank id does not match its document';
    }
    if (!isLook(r)) return 'not a rig state look';
    return lookDocId(r.look.id) === docId ? null : 'look id does not match its document';
  },
  legacyLibrary(body) {
    const lib = (body as { library?: RigStateLibrary } | null)?.library;
    return lib && Array.isArray(lib.banks) ? lib : null;
  },
};
